import { Box, Button, Card, Checkbox, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import PageLayout from 'components/layout/PageLayout';
import { INCOME_ENTRIES_URL } from 'constants/urls';
import { INCOME_ENTRY_STATUS, ROUTES } from 'constants/index'
import { formatDateOnlyDMY } from 'helpers';
import { formatIncomeEntryMeters, REVIEW_STATUS_MAP } from 'helpers/incomeEntry';
import useCrud from 'hooks/useCrud';
import { Fragment, useEffect, useState } from 'react'
import { MdCheck, MdExpandMore, MdInfoOutline, MdWarningAmber } from 'react-icons/md';
import { useParams, useNavigate } from 'react-router-dom'
import { IIncomeEntry, IIncomeEntryArticle, IIncomeEntryArticleBundle } from 'types';
import { calculateProgress } from 'helpers/incomeEntry';
import { axiosPut } from 'api';
import { useToast } from 'hooks/useToast';
import IncomeEntryArticleObservationsList from 'components/IncomeEntryReview/IncomeEntryArticleObservationsList';
import { typographyStyles } from 'constants/styles';

export default function IncomeEntryDetailReviewPage() {
  const { id } = useParams();
  const { 
    fetchData,
    loading,
  } = useCrud<IIncomeEntry>(INCOME_ENTRIES_URL);
  const { showSnackBar } = useToast();
  const navigate = useNavigate();
  
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [updatingData, setUpdatingData] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [notifySupplier, setNotifySupplier] = useState(true);

  const toggleExpand = (articleId: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(articleId)) {
        next.delete(articleId);
      } else {
        next.add(articleId);
      }
      return next;
    });
  };

  const [incomeEntryData, setIncomeEntryData] = useState<IIncomeEntry | null>(null);
  const [incomeEntryArticles, setIncomeEntryArticles] = useState<IIncomeEntryArticle[]>([]);
  const reviewStatus = REVIEW_STATUS_MAP[incomeEntryData?.result ?? 'ok'];
  const digitalAmount = incomeEntryArticles.reduce((sum, article) => sum + (article.amount ?? 0), 0);

  const {
    totalArticles,
    totalBundles,
    resolvedBundles,
    totalObservations,
    fisicalAmount,
    availableAmount,
    unavailableAmount,
  } = calculateProgress(incomeEntryArticles);

  useEffect(() => {
    if(!id){
      return;
    }
    fetchData(id)
      .then((r: {data: IIncomeEntry | null; status: number}) => {
        setMode(r.data?.status === INCOME_ENTRY_STATUS.FINISHED ? "view" : "edit")
        setIncomeEntryData(r.data ?? null);
        setIncomeEntryArticles(r.data?.articles ?? []);
      })
  }, [id]);

  const handleBundleChange = (articleId: number, updatedBundle: IIncomeEntryArticleBundle) => {
    setIncomeEntryArticles((prev) =>
      prev.map((article) =>
        article.id !== articleId
          ? article
          : {
              ...article,
              bundles: article.bundles.map((bundle) =>
                bundle.id === updatedBundle.id ? updatedBundle : bundle
              ),
            }
      )
    );
  };

  const handleConfirm = () => {
    if(mode === 'view') return;
    setNotifySupplier(true);
    setConfirmModalOpen(true);
  };

  const performConfirm = async () => {
    if(mode === 'view') return;
    setUpdatingData(true);
    try {
      const r = await axiosPut(`${INCOME_ENTRIES_URL}/${id}/review`, {
        notify_supplier: notifySupplier,
        incomeEntry:  { ...incomeEntryData, articles: incomeEntryArticles }
      });
      if(r.status === 200){
        showSnackBar("Revisión confirmada correctamente", "success");
        navigate(ROUTES.INCOME_ENTRY);
        return;
      } else {
        showSnackBar("Error al confirmar la revisión", "error");
        return;
      }
    } catch(err){
      showSnackBar("Error al confirmar la revisión", "error");
    } finally {
      setUpdatingData(false);
      setConfirmModalOpen(false);
    }
  };

  if(loading){
    return (
      <PageLayout
        pageTitle="Ingresos"
        pageDesc="Verificá la mercadería contra el remito"
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 8, textAlign: "center" }}
        >
          Cargando...
        </Typography>
      </PageLayout>
    );
  }

  if(!incomeEntryData){
    return (
      <PageLayout
        pageTitle="IngresosConfirmado en planta con anomalia"
        pageDesc="Verificá la mercadería contra el remito"
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 8, textAlign: "center" }}
        >
          No data available
        </Typography>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      pageTitle={(
        <Stack direction="row" width={"100%"} spacing={2} justifyContent={'flex-start'} alignItems={'center'}>
          <Typography variant="h4" fontWeight="bold">
            Orden de ingreso {incomeEntryData?.numero_remito ?? ''}
          </Typography>
          {mode === 'view' && (
            <Chip
            icon={<MdInfoOutline />}
            label={"Preingreso finalizado"}
            color="secondary"
            variant="outlined"
          />
          )}
          <Box sx={{ flexGrow: 1, textAlign: "end" }}>
            <Chip
              icon={<MdCheck />}
              label={reviewStatus.label}
              color={reviewStatus.color}
              variant="outlined"
              
            />
          </Box>
        </Stack>
      )}
      pageDesc={`${incomeEntryData?.client?.razon_social} · remito digital + verificación física`}
    >
      <Stack direction="row" spacing={2} width={"100%"} mb={2}>
        <Card
          variant='outlined'
          sx={{
            py: 2,
            px: 3,
            borderWidth: 1.5,
            borderColor: "divider",
          }}
        >
          <Typography>
            📄 Origen: PDF interpretado por IA
          </Typography>
          <Typography fontWeight="bold">
            {totalArticles} artículos · {digitalAmount} unidades totales
          </Typography>
          <Typography color="success.main">
            Fecha de ingreso — {formatDateOnlyDMY(incomeEntryData?.fecha_ingreso)}
          </Typography>
        </Card>
        <Card
          variant='outlined'
          sx={{
            py: 2,
            px: 3,
            borderWidth: 1.5,
            borderColor: "divider",
          }}
        >
          <Typography>
            📦 Verificación física
          </Typography>
          <Typography fontWeight="bold">
            {resolvedBundles} de {totalBundles} fardos verificados {totalObservations ? ` · ${totalObservations} con observación` : ''}
          </Typography>
          <Typography fontWeight="bold">
            {fisicalAmount.toLocaleString("es-AR")} unidades
          </Typography>
          <Typography color={`${reviewStatus.color}.main`}>
            {incomeEntryData?.result === "ok" ? "Confirmado en planta" : "Confirmado en planta con anomalías"} — {formatDateOnlyDMY(incomeEntryData?.updated_at)}
          </Typography>
          <Typography variant='body2'>
            🔗 Foto del remito firmado
          </Typography>
        </Card>
      </Stack>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell/>
            <TableCell>Producto</TableCell>
            <TableCell>Unidades Digital (Remito)</TableCell>
            <TableCell>Metros Digital (Remito)</TableCell>
            <TableCell>Unidades Físico (Verificado)</TableCell>
            <TableCell>Metros Físico (Verificado)</TableCell>
            <TableCell>Unidades Disponibles</TableCell>
            <TableCell>Metros Disponibles</TableCell>
            <TableCell>Codigo</TableCell>
            <TableCell>Match</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
        {incomeEntryArticles.map((rowArticle: IIncomeEntryArticle) => {
          const bundlesAmount = rowArticle.bundles.reduce((sum, bundle) => sum + (bundle.amount ?? 0), 0);
          const bundlesDiff = (rowArticle.amount - bundlesAmount) * -1;
          const match = bundlesAmount === rowArticle.amount;
          const expanded = expandedIds.has(rowArticle.id);
          const rowAvailableAmount = rowArticle.bundles.reduce((sum, bundle) => sum + (bundle.amount_available ?? 0), 0);
          const metersPerItem = rowArticle.article?.equivalence?.article_units_per_item;
          return (
            <Fragment key={rowArticle.id}>
              <TableRow>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => toggleExpand(rowArticle.id)}
                    aria-label={expanded ? 'Ocultar observaciones' : 'Ver observaciones'}
                  >
                    <MdExpandMore
                      size={20}
                      style={{
                        transform: expanded ? 'rotate(180deg)' : 'none',
                        transition: 'transform .18s',
                      }}
                    />
                  </IconButton>
                </TableCell>
                <TableCell>{rowArticle.article?.description ?? ''}</TableCell>
                <TableCell>{rowArticle.amount ?? ''}</TableCell>
                <TableCell>{formatIncomeEntryMeters(rowArticle.amount, metersPerItem)}</TableCell>
                <TableCell>
                  {bundlesAmount ?? ''}
                  {!match && (
                    <Typography variant='inherit' color="warning.main" ml={1} display={"inline"}>
                      {bundlesDiff > 0 ? `+${bundlesDiff}` : `${bundlesDiff}`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatIncomeEntryMeters(bundlesAmount, metersPerItem)}</TableCell>
                <TableCell>{rowAvailableAmount ?? ''}</TableCell>
                <TableCell>{formatIncomeEntryMeters(rowAvailableAmount, metersPerItem)}</TableCell>
                <TableCell>{rowArticle.external_code ?? ''}</TableCell>
                <TableCell align="center">
                  <Box color={match? 'success.main' : 'warning.main'}>
                    {match ? <MdCheck size={20} /> : <MdWarningAmber size={20} />}
                  </Box>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell
                  colSpan={10}
                  sx={{ py: 0, pr: 0, pl: 3 }}
                >
                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box >
                      <IncomeEntryArticleObservationsList
                        incomeEntryId={incomeEntryData.id}
                        mode={mode}
                        bundles={rowArticle.bundles}
                        articleUnitsPerItem={metersPerItem}
                        onBundleChange={(bundle) => handleBundleChange(rowArticle.id, bundle)}
                      />
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </Fragment>
          )
        })}
        </TableBody>
      </Table>
      <Stack direction="row" spacing={2} justifyContent={'space-between'} alignItems={'center'} mt={2}>
        <Typography>
          Se verificaron {fisicalAmount.toLocaleString('es-AR')} unidades, de las cuales {availableAmount.toLocaleString('es-AR')} están disponibles. Diferencia con el remito: {(fisicalAmount - digitalAmount) > 0 ? '+' : ''}{(fisicalAmount - digitalAmount).toLocaleString('es-AR')} unidades.
        </Typography>
        {mode === 'edit' && (
          <Button variant="contained" size='large' color={reviewStatus.color} onClick={handleConfirm} disabled={updatingData} sx={{display: 'flex', gap: 1}}>
            {updatingData ? 
              (<><CircularProgress size={20} color={reviewStatus.color} /> Confirmando...</>)
              : reviewStatus.finalButton
            }
          </Button>
        )}
      </Stack>
      <Dialog open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar revisión</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Confirmar la revisión e ingresar la mercadería a stock?
          </DialogContentText>
          <Stack spacing={1.5} mt={2}>
            <Typography variant="body2">
              El ingreso se procesará con las siguientes cantidades:
            </Typography>
            <Card elevation={0} sx={{ py: 1.5, px: 2, bgcolor: "divider" }}>
              <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                TOTAL VERIFICADO (FÍSICO)
              </Typography>
              <Typography variant='body2' sx={{...typographyStyles.monospace}}>
                {totalArticles} artículo{totalArticles !== 1 ? 's' : ''}
              </Typography>
              <Typography fontWeight={700}>
                {fisicalAmount.toLocaleString("es-AR")} unidades
              </Typography>
            </Card>
            <Stack direction="row" spacing={1.5}>
              <Card elevation={0} sx={{ flex: 1, py: 1.5, px: 2, bgcolor: "divider" }}>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.6, textAlign: "center", display: "block" }}>
                  DISPONIBLE
                </Typography>
                <Typography variant="h6" fontWeight={700} color="success.main" sx={{ textAlign: "center" }}>
                  {availableAmount.toLocaleString("es-AR")}
                </Typography>
              </Card>
              <Card elevation={0} sx={{ flex: 1, py: 1.5, px: 2, bgcolor: "divider" }}>
                <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.6, textAlign: "center", display: "block" }}>
                  NO DISPONIBLE
                </Typography>
                <Typography variant="h6" fontWeight={700} color="warning.main" sx={{ textAlign: "center" }}>
                  {unavailableAmount.toLocaleString("es-AR")}
                </Typography>
              </Card>
            </Stack>
            <Stack>
              Se obtuvieron {fisicalAmount.toLocaleString("es-AR")} un. sobre las {digitalAmount.toLocaleString("es-AR")} un. declaradas por el cliente.
            </Stack>
            <TextField
              label="Observación"
              placeholder="Agregá una observación (opcional)"
              multiline
              minRows={2}
              fullWidth
              value={incomeEntryData.observations}
              onChange={(e) => setIncomeEntryData(prev => {
                if (!prev) return null;
                return {
                  ...prev,
                  observations: e.target.value
                }
              })}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifySupplier}
                  onChange={(e) => setNotifySupplier(e.target.checked)}
                />
              }
              label="Enviar datos al proveedor"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmModalOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={performConfirm}
            disabled={updatingData}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  )
}
